import { useState, useEffect, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ModalForm } from '@/components/common/Modal'
import {
  TextInput, SelectInput, CheckboxInput, FormRow
} from '@/components/common/FormField'
import { scheduleService, departmentService, studentService, employeeService } from '@/services/api'
import toast from 'react-hot-toast'

const TYPE_OPTIONS = [
  { value: 'group', label: 'По классу' },
  { value: 'kafedra', label: 'По кафедре' },
  { value: 'otdel', label: 'По отделу' },
  { value: 'individual_student', label: 'Индив. студент' },
  { value: 'individual_employee', label: 'Индив. сотрудник' },
]

const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

const INITIAL = {
  name: '',
  schedule_type: 'group',
  group: '',
  departments: [],
  student: '',
  employee: '',
  start_time: '08:00',
  end_time: '17:00',
  allowed_late_minutes: 15,
  work_days: [0, 1, 2, 3, 4],
  is_active: true,
}

// Map frontend UI type → backend schedule_type
const toBackendType = (uiType) =>
  uiType === 'kafedra' || uiType === 'otdel' ? 'department' : uiType

// When loading an existing 'department' schedule, detect sub-type from departments list
const toUiType = (schedule, allDepts) => {
  if (schedule.schedule_type !== 'department') return schedule.schedule_type
  const ids = schedule.departments?.length
    ? schedule.departments
    : schedule.department ? [schedule.department] : []
  if (!ids.length || !allDepts?.length) return 'kafedra'
  const firstDept = allDepts.find((d) => d.id === ids[0])
  return firstDept?.department_type === 'otdel' ? 'otdel' : 'kafedra'
}

function PersonSearch({ label, placeholder, value, displayValue, onSelect, onClear, searchFn, renderItem }) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)

  const { data: results } = useQuery({
    queryKey: ['person-search', placeholder, search],
    queryFn: () => searchFn(search),
    enabled: search.length >= 2,
  })

  useEffect(() => {
    if (displayValue) setSearch(displayValue)
  }, [displayValue])

  const handleSelect = (item) => {
    onSelect(item)
    setSearch(renderItem(item).label)
    setOpen(false)
  }

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} <span className="text-red-500">*</span>
      </label>
      {value ? (
        <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
          <span className="flex-1 text-sm">{search}</span>
          <button type="button" onClick={() => { onClear(); setSearch('') }} className="text-gray-400 hover:text-red-500 text-lg leading-none">×</button>
        </div>
      ) : (
        <div className="relative">
          <input
            className="input w-full"
            placeholder={placeholder}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOpen(true) }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
          />
          {open && results?.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {results.map((item) => {
                const { label, sub } = renderItem(item)
                return (
                  <button
                    key={item.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b border-gray-50 last:border-0"
                    onMouseDown={() => handleSelect(item)}
                  >
                    <div className="font-medium">{label}</div>
                    {sub && <div className="text-xs text-gray-400">{sub}</div>}
                  </button>
                )
              })}
            </div>
          )}
          {open && search.length >= 2 && (!results || results.length === 0) && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-2 text-sm text-gray-400">
              Не найдено
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function ScheduleModal({ open, onClose, schedule = null }) {
  const qc = useQueryClient()
  const [form, setForm] = useState(INITIAL)
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [deptSearch, setDeptSearch] = useState('')
  const isEdit = !!schedule

  const { data: groups } = useQuery({
    queryKey: ['all-classes'],
    queryFn: () => departmentService.classes({ page_size: 500 }).then((r) => r.data.results || r.data),
    enabled: open,
  })

  const { data: depts } = useQuery({
    queryKey: ['all-departments'],
    queryFn: () => departmentService.list({ page_size: 200 }).then((r) => r.data.results || r.data),
    enabled: open,
  })

  useEffect(() => {
    if (open) {
      if (schedule) {
        const deptIds = schedule.departments?.length
          ? schedule.departments
          : schedule.department ? [schedule.department] : []
        const uiType = toUiType(schedule, depts)
        setForm({
          name: schedule.name || '',
          schedule_type: uiType,
          group: schedule.group || '',
          departments: deptIds,
          student: schedule.student || '',
          employee: schedule.employee || '',
          start_time: schedule.start_time || '08:00',
          end_time: schedule.end_time || '17:00',
          allowed_late_minutes: schedule.allowed_late_minutes ?? 15,
          work_days: schedule.work_days || [0, 1, 2, 3, 4],
          is_active: schedule.is_active ?? true,
        })
        if (schedule.student_name) setSelectedStudent({ id: schedule.student, full_name: schedule.student_name })
        if (schedule.employee_name) setSelectedEmployee({ id: schedule.employee, full_name: schedule.employee_name })
      } else {
        setForm(INITIAL)
        setSelectedStudent(null)
        setSelectedEmployee(null)
      }
      setErrors({})
      setDeptSearch('')
    }
  }, [open, schedule])

  const toggleDept = (deptId) => {
    setForm((f) => {
      const ids = f.departments.includes(deptId)
        ? f.departments.filter((d) => d !== deptId)
        : [...f.departments, deptId]
      return { ...f, departments: ids }
    })
    if (errors.departments) setErrors((er) => ({ ...er, departments: '' }))
  }

  const set = (key) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setForm((f) => {
      const next = { ...f, [key]: val }
      if (key === 'schedule_type') {
        next.group = ''; next.departments = []; next.student = ''; next.employee = ''
        setSelectedStudent(null); setSelectedEmployee(null)
      }
      return next
    })
    if (errors[key]) setErrors((er) => ({ ...er, [key]: '' }))
  }

  const toggleDay = (dayIndex) => {
    setForm((f) => {
      const days = f.work_days.includes(dayIndex)
        ? f.work_days.filter((d) => d !== dayIndex)
        : [...f.work_days, dayIndex].sort()
      return { ...f, work_days: days }
    })
  }

  const validate = () => {
    const e = {}
    if (!form.name.trim()) e.name = 'Обязательное поле'
    if (form.schedule_type === 'group' && !form.group) e.group = 'Выберите группу'
    if ((form.schedule_type === 'kafedra' || form.schedule_type === 'otdel') && form.departments.length === 0)
      e.departments = form.schedule_type === 'kafedra' ? 'Выберите хотя бы одну кафедру' : 'Выберите хотя бы один отдел'
    if (form.schedule_type === 'individual_student' && !form.student) e.student = 'Выберите студента'
    if (form.schedule_type === 'individual_employee' && !form.employee) e.employee = 'Выберите сотрудника'
    if (form.work_days.length === 0) e.work_days = 'Выберите хотя бы один рабочий день'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)

    const payload = { ...form, schedule_type: toBackendType(form.schedule_type) }
    if (form.schedule_type !== 'group') delete payload.group
    if (form.schedule_type !== 'kafedra' && form.schedule_type !== 'otdel') delete payload.departments
    if (form.schedule_type !== 'individual_student') delete payload.student
    if (form.schedule_type !== 'individual_employee') delete payload.employee

    try {
      if (isEdit) {
        await scheduleService.update(schedule.id, payload)
        toast.success('Расписание обновлено')
      } else {
        await scheduleService.create(payload)
        toast.success('Расписание добавлено')
      }
      qc.invalidateQueries(['schedules'])
      onClose()
    } catch (err) {
      const data = err.response?.data
      if (data) {
        const fieldErrors = {}
        Object.entries(data).forEach(([k, v]) => { fieldErrors[k] = Array.isArray(v) ? v[0] : v })
        setErrors(fieldErrors)
      } else {
        toast.error('Ошибка сохранения')
      }
    } finally {
      setLoading(false)
    }
  }

  const groupOpts = (groups || []).map((g) => ({ value: g.id, label: g.name }))

  const filteredDepts = useMemo(() => {
    let list = depts || []
    if (form.schedule_type === 'kafedra') list = list.filter((d) => (d.department_type || 'kafedra') === 'kafedra')
    else if (form.schedule_type === 'otdel') list = list.filter((d) => d.department_type === 'otdel')
    if (deptSearch.trim()) list = list.filter((d) => d.name.toLowerCase().includes(deptSearch.toLowerCase()))
    return list
  }, [depts, form.schedule_type, deptSearch])

  const isDeptType = form.schedule_type === 'kafedra' || form.schedule_type === 'otdel'
  const deptLabel = form.schedule_type === 'kafedra' ? 'Кафедра' : 'Отдел'

  return (
    <ModalForm
      open={open}
      onClose={onClose}
      title={isEdit ? 'Редактировать расписание' : 'Добавить расписание'}
      onSubmit={handleSubmit}
      loading={loading}
    >
      <TextInput
        label="Название" required
        value={form.name} onChange={set('name')}
        error={errors.name} placeholder="Стандартное расписание группы ИТ-21"
      />

      <SelectInput
        label="Тип расписания"
        value={form.schedule_type} onChange={set('schedule_type')}
        options={TYPE_OPTIONS}
      />

      {form.schedule_type === 'group' && (
        <SelectInput
          label="Группа" required
          value={form.group} onChange={set('group')}
          options={groupOpts} placeholder="Выберите группу"
          error={errors.group}
        />
      )}

      {isDeptType && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {deptLabel} <span className="text-red-500">*</span>
          </label>

          {/* Search */}
          <input
            type="text"
            className="input w-full mb-2 text-sm"
            placeholder={`Поиск по названию...`}
            value={deptSearch}
            onChange={(e) => setDeptSearch(e.target.value)}
          />

          {/* Checkbox list */}
          <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-gray-50">
            {filteredDepts.map((d) => (
              <label key={d.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={form.departments.includes(d.id)}
                  onChange={() => toggleDept(d.id)}
                  className="rounded border-gray-300 text-primary-600"
                />
                <span className="text-sm flex-1">{d.name}</span>
              </label>
            ))}
            {filteredDepts.length === 0 && (
              <p className="px-3 py-2 text-sm text-gray-400">
                {(depts || []).length === 0 ? 'Загрузка...' : 'Не найдено'}
              </p>
            )}
          </div>
          {form.departments.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">Выбрано: {form.departments.length}</p>
          )}
          {errors.departments && <p className="text-xs text-red-500 mt-1">{errors.departments}</p>}
        </div>
      )}

      {form.schedule_type === 'individual_student' && (
        <div>
          <PersonSearch
            label="Студент"
            placeholder="Введите ФИО студента (мин. 2 символа)..."
            value={form.student}
            displayValue={selectedStudent?.full_name || ''}
            onSelect={(s) => { setSelectedStudent(s); setForm(f => ({ ...f, student: s.id })); if (errors.student) setErrors(er => ({ ...er, student: '' })) }}
            onClear={() => { setSelectedStudent(null); setForm(f => ({ ...f, student: '' })) }}
            searchFn={(q) => studentService.list({ search: q, page: 1 }).then(r => r.data.results || [])}
            renderItem={(s) => ({ label: s.full_name, sub: `${s.class_name} · ${s.student_id}` })}
          />
          {errors.student && <p className="text-xs text-red-500 mt-1">{errors.student}</p>}
        </div>
      )}

      {form.schedule_type === 'individual_employee' && (
        <div>
          <PersonSearch
            label="Сотрудник"
            placeholder="Введите ФИО сотрудника (мин. 2 символа)..."
            value={form.employee}
            displayValue={selectedEmployee?.full_name || ''}
            onSelect={(emp) => { setSelectedEmployee(emp); setForm(f => ({ ...f, employee: emp.id })); if (errors.employee) setErrors(er => ({ ...er, employee: '' })) }}
            onClear={() => { setSelectedEmployee(null); setForm(f => ({ ...f, employee: '' })) }}
            searchFn={(q) => employeeService.list({ search: q, page: 1 }).then(r => r.data.results || [])}
            renderItem={(emp) => ({ label: emp.full_name, sub: `${emp.department_name} · ${emp.position_name}` })}
          />
          {errors.employee && <p className="text-xs text-red-500 mt-1">{errors.employee}</p>}
        </div>
      )}

      <FormRow>
        <TextInput
          label="Начало рабочего дня"
          type="time" value={form.start_time} onChange={set('start_time')}
        />
        <TextInput
          label="Конец рабочего дня"
          type="time" value={form.end_time} onChange={set('end_time')}
        />
      </FormRow>

      <TextInput
        label="Допустимое опоздание (мин)"
        type="number" min={0} max={120}
        value={form.allowed_late_minutes} onChange={set('allowed_late_minutes')}
      />

      {/* Work days selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Рабочие дни <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-1.5">
          {DAYS.map((label, i) => (
            <button
              key={i}
              type="button"
              onClick={() => toggleDay(i)}
              className={`flex-1 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                form.work_days.includes(i)
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {errors.work_days && (
          <p className="text-xs text-red-500 mt-1">{errors.work_days}</p>
        )}
      </div>

      <CheckboxInput
        label="Активное расписание"
        checked={form.is_active}
        onChange={set('is_active')}
      />
    </ModalForm>
  )
}
